/**
 * Workspace serialization and restore. Serializes the whole view - ship, date
 * range, export options, filters, derived signals, and plot configuration -
 * into the internal query-string wire format used by saved/shared content and
 * legacy links. The ordinary address bar contains only a clean app route; the
 * full working query is kept in per-tab session storage for refresh recovery.
 *
 * Two layers:
 *  - `buildQueryString` / `parsePlotState` / the `parse*Param` helpers are PURE
 *    (atoms in, string/object out) so they're unit-testable without a DOM.
 *  - `syncToUrl` / `restoreFromUrl` are the thin glue that touch `history`,
 *    `document.body`, and the column loader around that pure core.
 *
 * Every state-mutating call site across the app calls `syncToUrl()`; restoring
 * suspends those so the draft and clean route are written once at the end.
 */

import { BUILDER_EMBED } from './embed.ts';
import { serializeNumber, parseNumber } from '../features/plot/number-config.ts';
import { parseGaugeDials, parseGaugeLayout, serializeGauge } from '../features/plot/gauge-config.ts';
import { parseSankeyDisplay, parseSankeyNodes, serializeSankey } from '../features/plot/sankey-config.ts';
import {
  parseTableColumns,
  parseTableGroups,
  parseTableMeasures,
  parseTableMode,
  parseTablePaging,
  pruneTable,
  serializeTable,
} from '../features/plot/table-config.ts';
import {
  selectedShip,
  startDate,
  endDate,
  activePreset,
  exportFormat,
  fillMethod,
  frequencyValue,
  frequencyUnit,
  filterLogic,
  filters,
  filterExpr,
  filterExprError,
  filterMode,
  selectedCols,
  allColumns,
  activeTab,
  activeSection,
  assistantTab,
  activeModelTab,
  activeDataSource,
  sfocSelected,
  sfocMeasured,
  sfocLayout,
  modelShips,
  modelStart,
  modelEnd,
  modelPreset,
  derivedSignals,
  outliers,
  DEFAULT_OUTLIERS,
  type FilterRow,
  type DerivedSignal,
  type OutlierState,
  type OutlierMethod,
  type DatePreset,
  comparisonShips,
  plottedShipIds,
  MAX_PLOT_SHIPS,
} from './store.ts';
import { activeFlags, hasFlag } from './feature-flags.ts';
import { isPageVisible } from './page-visibility.ts';
import { GRID_COLUMNS_MAX } from '../features/plot/render/render-grid.ts';
import { DIVERGING_DEFAULT } from '../features/plot/plot-constants.ts';
import { activeDashboardId, dashboardRoute } from '../features/dashboard/dashboard-state.ts';
import { DEFAULT_SOURCE } from './data-sources.ts';
import {
  plotState,
  FONT_SIZE_DEFAULT,
  FONT_SIZE_MIN,
  FONT_SIZE_MAX,
  PIE_BANDS_DEFAULT,
  PIE_BANDS_MIN,
  PIE_BANDS_MAX,
  parseBandEdges,
  autoAxisTitle,
  type AxisTitle,
  type PlotState,
  type Fit,
} from './plot-state.ts';
import { getFilters, getFrequency, exprKindOf } from './request.ts';
import { validateExpr } from '../features/filters/expr-parser.ts';
import { slicerRows } from '../features/plot/slicers.ts';
import { applyDatePreset, setPresetActive } from '../features/ship/date-presets.ts';
import { loadColumns } from '../features/ship/ship-data.ts';
import { ensureShipColumns } from '../features/ship/ship-columns.ts';
import { scheduleEstimate } from './estimate.ts';
import { offsetDays, presetWindow, toDatetimeLocal } from '../utils/dates.ts';
import type { FilterOp } from '../api/types.ts';
import {
  clearPeriodComparison,
  comparisonIssues,
  parsePeriodComparison,
  periodComparison,
  periodComparisonNotice,
  serializePeriodComparison,
  comparisonReady,
} from './period-comparison.ts';
import { buildAppPath, sectionRoute, type SectionRoute } from './app-route.ts';

const FILTER_OPS: readonly FilterOp[] = ['==', '!=', '>', '<', '>=', '<='];

/* ── syncToUrl: atoms → query string ─────────────────────── */

// While restoring (or loading a preset), every atom write would otherwise fire
// its own `syncToUrl()`. Suspend them and write once at the end.
let suspendDepth = 0;

/**
 * Whether the initial `restoreFromUrl()` has run.
 *
 * Until it has, `syncToUrl()` must not write: it serializes current atom state,
 * and before restore that state is defaults. Any
 * component that syncs during its first render would therefore erase the very
 * link the app is booting from - which is exactly what happened to shared plot
 * links when `<dashboard-view>` started syncing from `updated()`.
 *
 * `suspendDepth` cannot cover this: it guards the inside of a restore, not the
 * window before one starts.
 */
let urlRestored = false;

/** Per-tab draft used to restore the workspace while the address bar stays
 * human-sized. Saved views and share links continue to use the same query
 * string, but it no longer has to be visible. */
export const WORKING_VIEW_KEY = 'neptune_working_view_v1';

export function readWorkingView(
  storage: Pick<Storage, 'getItem'> = sessionStorage,
): string | null {
  try {
    const query = storage.getItem(WORKING_VIEW_KEY)?.replace(/^\?/, '') ?? '';
    return query || null;
  } catch {
    return null;
  }
}

export function clearWorkingView(
  storage: Pick<Storage, 'removeItem'> = sessionStorage,
): void {
  try {
    storage.removeItem(WORKING_VIEW_KEY);
  } catch {
    // A browser refusing session storage leaves refresh recovery unavailable;
    // the live in-memory workspace continues to work.
  }
}

function saveWorkingView(query: string): void {
  try {
    sessionStorage.setItem(WORKING_VIEW_KEY, query);
  } catch {
    // As above: the address stays clean even when recovery cannot be stored.
  }
}

/** A legacy query is an explicit portable view. `ff` by itself only enables a
 * development feature and should not replace this tab's saved workspace. */
export function legacyViewQuery(search: string): string | null {
  const query = search.replace(/^\?/, '');
  const params = new URLSearchParams(query);
  if (![...params.keys()].some((key) => key !== 'ff')) return null;
  return query;
}

/** True once the app has read the launch URL. Exposed for tests. */
export function isUrlRestored(): boolean {
  return urlRestored;
}

/* Append helpers - each writes one param group onto `params`. Grouped so
 * `buildQueryString` can emit only the groups the active tab actually consumes,
 * keeping every shared link a complete-but-minimal description of its view. */

function appendShip(params: URLSearchParams): void {
  const ship = selectedShip.value;
  if (ship) params.set('ship', ship);
  // Comparison ships are context alongside `ship`, not part of the chart recipe.
  // Omitted entirely when plotting a single ship, so old links stay byte-identical.
  const extras = periodComparison.value
    ? []
    : comparisonShips.value.filter((s) => s && s !== ship);
  if (extras.length) params.set('ships', extras.join(','));
}

/** Data source - written only when it is not EOS, the default. */
function appendDataSource(params: URLSearchParams): void {
  if (activeDataSource.value !== DEFAULT_SOURCE) params.set('ds', activeDataSource.value);
}

function appendDates(params: URLSearchParams): void {
  if (periodComparison.value) {
    params.set('start', startDate.value);
    params.set('end', endDate.value);
  } else if (activePreset.value !== 'custom') {
    params.set('preset', activePreset.value);
  } else {
    params.set('start', startDate.value);
    params.set('end', endDate.value);
  }
}

/** Data-shaping group shared by Export and Plot: resampling, filters, derived
 *  signals, and outliers - everything that shapes the fetched rows. */
function appendDataShaping(params: URLSearchParams): void {
  const freq = getFrequency();
  if (freq) params.set('freq', freq);
  if (fillMethod.value !== 'none') params.set('fill', fillMethod.value);
  if (filterLogic.value !== 'AND') params.set('logic', filterLogic.value);

  // RHS is `val` for a literal, or `@col` when comparing against another
  // column. Raw column names never contain `@` or `|`, so this stays cheap and
  // backward compatible with old `col|op|val` links. A slicer is `col|~|min~max`
  // (either end may be empty = open), so it reopens as a slicer rather than as
  // the >= / <= pair it is sent to the server as.
  const fg = getFilters(undefined, { omitSlicers: true });
  const filterParts = (fg?.conditions ?? []).map(
    (f) => `${f.column}|${f.op}|${f.value_column ? '@' + f.value_column : f.value}`,
  );
  for (const r of slicerRows()) {
    filterParts.push(`${r.column}|~|${r.slicer.min ?? ''}~${r.slicer.max ?? ''}`);
  }
  if (filterParts.length) params.set('filters', filterParts.join(';;'));
  const fexpr = filterExpr.value.trim();
  if (fexpr) params.set('fexpr', fexpr);
  // Rows and an expression are both live now. Older links meant "only one of
  // them", so any link with an expression says `and` - the restore below (and a
  // dashboard tile) reads a link without it the old way.
  if (fexpr) params.set('fmode', filterMode.value === 'expr' ? 'expr' : 'and');

  // Derived signals: name|op|aMode|a|bMode|b joined by ',,'. Pipes are safe -
  // derived names are regex-gated and raw column names never contain pipes.
  // Expression-mode rows serialize as name|EXPR|<expression> instead.
  const derived = derivedSignals.value;
  if (derived.length) {
    params.set(
      'd',
      derived
        .map((d) => {
          if (d.expr != null && d.expr.trim()) return `${d.name}|EXPR|${d.expr}`;
          const aMode = d.operand_a_const != null ? 'n' : 'c';
          const a = aMode === 'c' ? d.operand_a || '' : String(d.operand_a_const ?? 0);
          const bMode = d.operand_b_col != null ? 'c' : 'n';
          const b = bMode === 'c' ? d.operand_b_col || '' : String(d.operand_b_const ?? 0);
          return [d.name, d.op, aMode, a, bMode, b].join('|');
        })
        .join(',,'),
    );
  }

  // Outliers: method|action|col1,col2|k=…;ns=…;pl=…;ph=…;w=… - only when active (a
  // method is chosen AND ≥1 target column), so the default off-state stays absent.
  const ol = outliers.value;
  if (ol.method !== 'none' && ol.columns.length) {
    const op = [`k=${ol.k}`, `ns=${ol.nSigma}`, `pl=${ol.pLow}`, `ph=${ol.pHigh}`, `w=${ol.window}`].join(';');
    params.set('ol', `${ol.method}|${ol.action}|${ol.columns.join(',')}|${op}`);
  }
}

/** Export-only params: output format and the export column selection. */
function appendExportOnly(params: URLSearchParams): void {
  if (exportFormat.value !== 'csv') params.set('format', exportFormat.value);
  if (selectedCols.value.size > 0) {
    params.set('sel', [...selectedCols.value].join(','));
  }
}

/** Plot-only params: the full chart configuration (type, axes, style, fits…). */
function appendPlotConfig(params: URLSearchParams): void {
  const s = plotState.value;
  // Not until B exists: A alone is comparison being set up, and a `pc=` with no
  // periods would be refused when the link is opened.
  if (comparisonReady()) params.set('pc', serializePeriodComparison(periodComparison.value!));
  if (s.plot_type && s.plot_type !== 'line') params.set('pt', s.plot_type);
  // The X default is the literal `timestampNDC` - every source's time column.
  if (s.x_column && s.x_column !== 'timestampNDC') params.set('x', s.x_column);
  if (s.y_left.length) params.set('yl', s.y_left.join(','));
  if (s.y_right.length) params.set('yr', s.y_right.join(','));
  if (s.hist_columns.length) params.set('hc', s.hist_columns.join(','));
  if (s.hist_bins !== 40) params.set('bins', String(s.hist_bins));
  if (s.plot_type === 'heatmap') {
    if (s.y_column) params.set('hy', s.y_column);
    if (s.z_column && s.z_agg) {
      params.set('hz', s.z_column);
      if (s.z_agg !== 'avg') params.set('hagg', s.z_agg);
    }
    if (s.heat_bins) params.set('hb', String(s.heat_bins));
    if (s.heat_x_step) params.set('hxs', String(s.heat_x_step));
    if (s.heat_y_step) params.set('hys', String(s.heat_y_step));
  }
  if (s.plot_type === 'correlation') {
    if (s.corr_columns.length) params.set('cc', s.corr_columns.join(','));
    if (s.corr_method !== 'pearson') params.set('cmeth', s.corr_method);
    if (s.corr_order !== 'given') params.set('cord', s.corr_order);
  }
  if (s.plot_type === 'number') serializeNumber(s, params);
  if (s.plot_type === 'gauge') serializeGauge(s, params);
  if (s.plot_type === 'sankey') serializeSankey(s, params);
  if (s.plot_type === 'table') serializeTable(s, params);
  if (s.plot_type === 'pie') {
    if (s.pie_mode !== 'signals') params.set('pmo', s.pie_mode);
    if (s.pie_mode === 'bands') {
      if (s.pie_band_column) params.set('pbc', s.pie_band_column);
      if (s.pie_band_edges?.length) params.set('pbe', s.pie_band_edges.join(','));
      else if (s.pie_bands !== PIE_BANDS_DEFAULT) params.set('pbn', String(s.pie_bands));
    } else {
      if (s.pie_columns.length) params.set('pcol', s.pie_columns.join(','));
      if (s.pie_agg !== 'avg') params.set('pagg', s.pie_agg);
    }
  }
  if (s.plot_type === 'box') {
    if (s.box_mode !== 'signals') params.set('bmo', s.box_mode);
    if (s.box_mode === 'groups') {
      if (s.box_column) params.set('bcol', s.box_column);
      if (s.box_group_column) params.set('bgrp', s.box_group_column);
    } else if (s.box_columns.length) {
      params.set('bcols', s.box_columns.join(','));
    }
  }
  if (s.style.corr_triangle) params.set('ctri', '1');
  if (!s.style.pie_donut) params.set('pdo', '0');
  if (s.style.font_size !== FONT_SIZE_DEFAULT) params.set('fsz', String(s.style.font_size));
  if (s.style.heat_scale !== 'auto') params.set('hsc', s.style.heat_scale);
  if (s.show_legs) params.set('legs', '1');
  if (s.show_changeovers) params.set('co', '1');
  if (s.style.show_points) params.set('pts_show', '1');
  if (s.style.connect_gaps) params.set('gaps', '1');
  if (!s.style.show_thresholds) params.set('thr', '0');
  if (s.style.line_width !== 2) params.set('lw', String(s.style.line_width));
  if (s.style.log_scale) params.set('log', '1');
  if (s.style.scatter_alpha !== 0.7) params.set('sa', String(s.style.scatter_alpha));
  if (s.style.scatter_point_size !== 3) params.set('ps', String(s.style.scatter_point_size));
  if (s.style.show_diagonal) params.set('diag', '1');
  if (s.view_mode !== 'overlay') params.set('vm', s.view_mode);
  if (s.style.color_mode !== 'hue-signal') params.set('cm', s.style.color_mode);
  if (s.style.focus_hover) params.set('fh', '1');
  if (s.style.show_legend) params.set('lg', '1');
  if (!s.style.legend_tools) params.set('lgt', '0');
  if (!s.style.grid_shared_y) params.set('gsy', '0');
  if (s.style.grid_columns) params.set('gcols', String(s.style.grid_columns));
  if (s.style.precision_zoom) params.set('pz', '1');
  if (s.style.live_layout !== 'now') params.set('ll', s.style.live_layout);
  if (!s.style.live_now_label) params.set('lnl', '0');
  if (s.style.live_motion !== 'smooth') params.set('lm', s.style.live_motion);

  const colorKeys = Object.keys(s.custom_colors);
  if (colorKeys.length) {
    params.set(
      'colors',
      colorKeys.map((col) => `${col}:${(s.custom_colors[col] ?? '').replace('#', '')}`).join(','),
    );
  }
  // color_by + color_scale apply to line, scatter and map; inert for the rest.
  // Line was left out when it could not be coloured, which silently dropped the
  // column from every shared link, saved view, catalog chart and dashboard tile
  // the moment a line could.
  if (
    (s.plot_type === 'line' || s.plot_type === 'scatter' || s.plot_type === 'map') &&
    s.color_by
  ) {
    params.set('cb', s.color_by);
    if (s.color_scale !== 'Viridis') params.set('cs', s.color_scale);
  }
  // A heatmap and a correlation matrix *are* their ramp - there is no `cb` to
  // hang it off, so it is carried on its own or the link comes back in the
  // wrong colours.
  if (
    (s.plot_type === 'heatmap' ||
      s.plot_type === 'correlation' ||
      // Bands are ordered, so they are coloured off the ramp; signals use the
      // series palette and have no ramp to carry.
      (s.plot_type === 'pie' && s.pie_mode === 'bands')) &&
    s.color_scale &&
    s.color_scale !== 'Viridis'
  ) {
    params.set('cs', s.color_scale);
  }

  // Map: lat/lon columns + basemap toggles (track on by default, nautical off).
  if (s.plot_type === 'map') {
    if (s.lat_column) params.set('lat', s.lat_column);
    if (s.lon_column) params.set('lon', s.lon_column);
    if (s.map_nautical) params.set('naut', '1');
    if (!s.map_show_track) params.set('trk', '0');
  }

  // Fits: series|kind|p1=v1;p2=v2|color(hex-without-#); color slot omitted on default.
  if (s.fits.length) {
    params.set(
      'f',
      s.fits
        .map((f) => {
          const p = Object.entries(f.params || {})
            .map(([k, v]) => `${k}=${v}`)
            .join(';');
          const parts = [f.series, f.kind, p];
          if (f.color) parts.push(f.color.replace('#', ''));
          return parts.join('|');
        })
        .join(',,'),
    );
  }

  const ab = s.axis_bounds;
  if (ab.y_min != null) params.set('ymin', String(ab.y_min));
  if (ab.y_max != null) params.set('ymax', String(ab.y_max));
  if (ab.yr_min != null) params.set('yrmin', String(ab.yr_min));
  if (ab.yr_max != null) params.set('yrmax', String(ab.yr_max));
  if (ab.x_min != null) params.set('xmino', String(ab.x_min));
  if (ab.x_max != null) params.set('xmaxo', String(ab.x_max));

  const yt = serializeAxisTitle(s.axis_titles.y);
  if (yt) params.set('yt', yt);
  const yrt = serializeAxisTitle(s.axis_titles.yr);
  if (yrt) params.set('yrt', yrt);
}

/**
 * One Y axis title on the wire: absent for auto, `off`, `s:<a>,<b>` for picked
 * signals (comma-joined like `yl`), `t:<text>` for the user's own words.
 */
export function serializeAxisTitle(t: AxisTitle): string {
  if (t.mode === 'off') return 'off';
  if (t.mode === 'signals' && t.signals.length) return `s:${t.signals.join(',')}`;
  if (t.mode === 'text' && t.text.trim()) return `t:${t.text.trim()}`;
  return '';
}

export function parseAxisTitle(v: string | null): AxisTitle {
  if (v === 'off') return { ...autoAxisTitle(), mode: 'off' };
  if (v?.startsWith('s:')) return { ...autoAxisTitle(), mode: 'signals', signals: v.slice(2).split(',').filter(Boolean) };
  if (v?.startsWith('t:')) return { ...autoAxisTitle(), mode: 'text', text: v.slice(2) };
  return autoAxisTitle();
}

/** The Model Viewer's shared ships and window (every model reads them). A named
 *  window is written by name so it keeps rolling, like Explore's. */
function appendModelSelection(params: URLSearchParams): void {
  if (modelShips.value.length) params.set('mvs', modelShips.value.join(','));
  if (modelPreset.value !== 'custom') {
    params.set('mvp', modelPreset.value);
  } else {
    params.set('mvst', modelStart.value);
    params.set('mven', modelEnd.value);
  }
}

/** SFOC's selected curve keys. The curve date is not written - it is the
 *  window's last day. */
function appendSfoc(params: URLSearchParams): void {
  const sel = [...sfocSelected.value];
  if (sel.length) params.set('sfs', sel.join(','));
  if (sfocMeasured.value !== 'scatter') params.set('sfm', sfocMeasured.value);
  if (sfocLayout.value === 'grid') params.set('sfl', 'grid');
}

/**
 * Build the query string for the current view (no leading `?`). Pure: reads the
 * atoms, writes nothing. Only non-default values are emitted, and only the param
 * groups the ACTIVE TAB consumes - so an SFOC link carries no plot/date config,
 * an Export link no plot config, etc. In-memory atoms keep the rest of the state
 * across tab switches within a session.
 */
export function buildQueryString(): string {
  const params = new URLSearchParams();
  const tab = activeTab.value;

  // Feature flags remain part of the portable wire format as well as the clean
  // route's one allowed query parameter.
  if (activeFlags.size) params.set('ff', [...activeFlags].join(','));
  // Section marker - `explore` is the default and stays implicit. The Explore
  // params are still emitted alongside it, because Dashboards shares the ship
  // and date range and a link back to Explore should land where you left it.
  if (activeSection.value !== 'explore') params.set('section', activeSection.value);
  // The open dashboard, so a dashboard is a link. Absent while browsing the
  // list, which is what makes a bare `/dashboards` land on the list.
  // Which part of the assistant - `chat` is the default and stays implicit, so
  // a plain `/assistant` route lands on the conversation.
  if (activeSection.value === 'assistant' && assistantTab.value !== 'chat') {
    params.set('atab', assistantTab.value);
  }
  if (activeSection.value === 'dashboards' && dashboardRoute.value === 'view') {
    const id = activeDashboardId.value;
    if (id) params.set('db', id);
  }
  // Tab marker - `export` is the default and stays implicit.
  // Plot is the default tab, so it is implicit; every other tab is explicit.
  if (tab !== 'plot') params.set('tab', tab);

  if (tab === 'export' || tab === 'plot') {
    appendShip(params);
    appendDataSource(params);
    appendDates(params);
    appendDataShaping(params);
    if (tab === 'export') appendExportOnly(params);
    else appendPlotConfig(params);
  } else if (tab === 'modelviewer') {
    // Every model reads the Model Viewer's own ships and window, not Explore's.
    appendModelSelection(params);
    if (activeModelTab.value === 'sfoc') {
      params.set('mt', 'sfoc');
      appendSfoc(params);
    }
  }
  // assistant: nothing beyond `tab` (+ `ff`).

  return params.toString();
}

function visibleFeatureQuery(): string {
  return activeFlags.size ? `?ff=${encodeURIComponent([...activeFlags].join(','))}` : '';
}

/** Write the current destination to the address bar and keep the complete view
 * in this tab's session storage. An optional visible URL preserves an inbound
 * `/s/…` or `/c/…` permalink during its initial restore. */
export function syncToUrl(visibleUrl?: string): void {
  if (suspendDepth > 0) return;
  // Never write before the launch URL has been read - see `urlRestored`.
  if (!urlRestored) return;
  // The chart builder's frame owns neither the address bar nor the tab's
  // working draft (it shares the page's sessionStorage) - `embed.ts`.
  if (BUILDER_EMBED) return;
  // No-op outside a browser (e.g. the node test runner), like the store's
  // `persisted()` localStorage guard.
  if (typeof history === 'undefined' || typeof window === 'undefined') return;
  const qs = buildQueryString();
  saveWorkingView(qs);
  const path = buildAppPath({
    section: activeSection.value,
    tab: activeTab.value,
    modelTab: activeModelTab.value,
    assistantTab: assistantTab.value,
    dashboardId: activeDashboardId.value,
    dashboardOpen: dashboardRoute.value === 'view',
  });
  history.replaceState(
    { workspaceQuery: qs },
    '',
    visibleUrl ?? `${path}${visibleFeatureQuery()}`,
  );
}

/* ── Pure parsers (query string → plain values) ──────────── */

/**
 * Apply the plot-state params from `params` onto a copy of `base`, returning the
 * new state. Pure - no atoms, no DOM. (Derived signals and dates/filters are
 * handled separately because they live in their own atoms.)
 */
export function parsePlotState(params: URLSearchParams, base: PlotState): PlotState {
  const s: PlotState = {
    ...base,
    y_left: [...base.y_left],
    y_right: [...base.y_right],
    hist_columns: [...base.hist_columns],
    corr_columns: [...base.corr_columns],
    pie_columns: [...base.pie_columns],
    box_columns: [...base.box_columns],
    table_columns: [...base.table_columns],
    table_groups: [...base.table_groups],
    table_measures: [...base.table_measures],
    gauge_dials: base.gauge_dials.map((d) => ({ ...d, zones: d.zones.map((z) => ({ ...z })) })),
    sankey_nodes: base.sankey_nodes.map((n) => ({ ...n, to: [...n.to] })),
    sankey_display: { ...base.sankey_display },
    pie_band_edges: base.pie_band_edges ? [...base.pie_band_edges] : null,
    style: { ...base.style },
    custom_colors: { ...base.custom_colors },
    fits: [...base.fits],
    axis_bounds: { ...base.axis_bounds },
  };

  const pt = params.get('pt');
  if (
    pt === 'line' ||
    pt === 'scatter' ||
    pt === 'histogram' ||
    pt === 'heatmap' ||
    pt === 'correlation' ||
    pt === 'pie' ||
    pt === 'box' ||
    pt === 'number' ||
    pt === 'gauge' ||
    pt === 'sankey' ||
    pt === 'table' ||
    pt === 'map'
  ) {
    s.plot_type = pt;
  }
  const lat = params.get('lat');
  parseNumber(params, s);
  const gd = parseGaugeDials(params.get('gd'));
  if (gd) s.gauge_dials = gd;
  const glay = parseGaugeLayout(params.get('glay'));
  if (glay) s.gauge_layout = glay;
  const sk = parseSankeyNodes(params.get('sk'));
  if (sk) s.sankey_nodes = sk;
  if (pt === 'sankey') s.sankey_display = parseSankeyDisplay(params);
  if (pt === 'table') {
    s.table_mode = parseTableMode(params);
    const paging = parseTablePaging(params);
    s.table_page_size = paging.size;
    s.table_page = paging.page;
  }
  const tcols = parseTableColumns(params.get('tcols'));
  if (tcols) s.table_columns = tcols;
  const tg = parseTableGroups(params.get('tg'));
  if (tg) s.table_groups = tg;
  const tm = parseTableMeasures(params.get('tm'));
  if (tm) s.table_measures = tm;
  const tsort = params.get('tsort');
  if (tsort && /^(k[01]|ship|n|m\d{1,2}):(asc|desc)$/.test(tsort)) s.table_sort = tsort;
  if (lat) s.lat_column = lat;
  const lon = params.get('lon');
  if (lon) s.lon_column = lon;
  if (params.get('naut') === '1') s.map_nautical = true;
  if (params.get('trk') === '0') s.map_show_track = false;
  const x = params.get('x');
  if (x) s.x_column = x;
  const yl = params.get('yl');
  if (yl) s.y_left = yl.split(',').filter(Boolean);
  const yr = params.get('yr');
  if (yr) s.y_right = yr.split(',').filter(Boolean);
  const hc = params.get('hc');
  if (hc) s.hist_columns = hc.split(',').filter(Boolean);
  const bins = parseInt(params.get('bins') ?? '', 10);
  if (Number.isFinite(bins) && bins >= 5 && bins <= 200) s.hist_bins = bins;
  const cc = params.get('cc');
  if (cc) s.corr_columns = cc.split(',').filter(Boolean);
  const cmeth = params.get('cmeth');
  if (cmeth === 'pearson' || cmeth === 'spearman') s.corr_method = cmeth;
  const cord = params.get('cord');
  if (cord === 'given' || cord === 'cluster') s.corr_order = cord;
  if (params.get('ctri') === '1') s.style.corr_triangle = true;
  if (params.get('pdo') === '0') s.style.pie_donut = false;
  const bmo = params.get('bmo');
  if (bmo === 'signals' || bmo === 'groups') s.box_mode = bmo;
  const bcols = params.get('bcols');
  if (bcols) s.box_columns = bcols.split(',').filter(Boolean);
  const bcol = params.get('bcol');
  if (bcol) s.box_column = bcol;
  const bgrp = params.get('bgrp');
  if (bgrp) s.box_group_column = bgrp;
  const pmo = params.get('pmo');
  if (pmo === 'signals' || pmo === 'bands') s.pie_mode = pmo;
  const pcol = params.get('pcol');
  if (pcol) s.pie_columns = pcol.split(',').filter(Boolean);
  const pagg = params.get('pagg');
  if (pagg === 'avg' || pagg === 'sum') s.pie_agg = pagg;
  const pbc = params.get('pbc');
  if (pbc) s.pie_band_column = pbc;
  const pbn = parseInt(params.get('pbn') ?? '', 10);
  if (Number.isFinite(pbn)) s.pie_bands = Math.max(PIE_BANDS_MIN, Math.min(PIE_BANDS_MAX, pbn));
  const pbe = params.get('pbe');
  if (pbe) s.pie_band_edges = parseBandEdges(pbe).edges;
  const fsz = parseInt(params.get('fsz') ?? '', 10);
  if (Number.isFinite(fsz)) {
    s.style.font_size = Math.max(FONT_SIZE_MIN, Math.min(FONT_SIZE_MAX, fsz));
  }
  const hy = params.get('hy');
  if (hy) s.y_column = hy;
  const hz = params.get('hz');
  if (hz) {
    s.z_column = hz;
    // A link with a column but no explicit aggregate means the default one, not
    // density - `hagg` is only written when it differs.
    s.z_agg = 'avg';
  }
  const hagg = params.get('hagg');
  if (
    hagg === 'avg' ||
    hagg === 'median' ||
    hagg === 'min' ||
    hagg === 'max' ||
    hagg === 'sum' ||
    hagg === 'count'
  ) {
    s.z_agg = hagg;
  }
  const hb = parseInt(params.get('hb') ?? '', 10);
  if (Number.isFinite(hb) && hb >= 5 && hb <= 200) s.heat_bins = hb;
  const hxs = parseFloat(params.get('hxs') ?? '');
  if (Number.isFinite(hxs) && hxs > 0) s.heat_x_step = hxs;
  const hys = parseFloat(params.get('hys') ?? '');
  if (Number.isFinite(hys) && hys > 0) s.heat_y_step = hys;
  const hsc = params.get('hsc');
  if (hsc === 'linear' || hsc === 'log' || hsc === 'sqrt' || hsc === 'auto') {
    s.style.heat_scale = hsc;
  }
  if (params.get('legs') === '1') s.show_legs = true;
  if (params.get('co') === '1') s.show_changeovers = true;
  const cb = params.get('cb');
  if (cb) s.color_by = cb;
  const cs = params.get('cs');
  if (cs) s.color_scale = cs;
  // A correlation link that names no ramp gets the diverging default, the same
  // one `setPlotType` moves to - a signed matrix on a sequential ramp hides the
  // sign, and an old link is not a reason to show it wrongly. Recorded the same
  // way too, so switching away from the matrix - drilling into a pair, say -
  // puts the ordinary ramp back rather than leaving a density heatmap looking
  // signed.
  else if (s.plot_type === 'correlation') {
    s.ramp_before_corr = base.color_scale;
    s.color_scale = DIVERGING_DEFAULT;
  }

  if (params.get('pts_show') === '1') s.style.show_points = true;
  if (params.get('gaps') === '1') s.style.connect_gaps = true;
  if (params.get('thr') === '0') s.style.show_thresholds = false;
  const lw = parseFloat(params.get('lw') ?? '');
  if (Number.isFinite(lw)) s.style.line_width = Math.max(1, Math.min(8, Math.round(lw)));
  if (params.get('log') === '1') s.style.log_scale = true;
  const sa = parseFloat(params.get('sa') ?? '');
  if (Number.isFinite(sa) && sa >= 0 && sa <= 1) s.style.scatter_alpha = sa;
  const ps = parseFloat(params.get('ps') ?? '');
  if (Number.isFinite(ps)) s.style.scatter_point_size = Math.max(1, Math.min(8, Math.round(ps)));
  if (params.get('diag') === '1') s.style.show_diagonal = true;
  const vm = params.get('vm');
  if (vm === 'overlay' || vm === 'grid') s.view_mode = vm;
  const cm = params.get('cm');
  if (cm === 'hue-signal' || cm === 'hue-ship' || cm === 'focus' || cm === 'unique') {
    s.style.color_mode = cm;
  }
  if (params.get('fh') === '1') s.style.focus_hover = true;
  if (params.get('lg') === '1') s.style.show_legend = true;
  if (params.get('lg') === '0') s.style.show_legend = false;
  if (params.get('lgt') === '0') s.style.legend_tools = false;
  if (params.get('gsy') === '0') s.style.grid_shared_y = false;
  const gcols = Number(params.get('gcols'));
  if (Number.isInteger(gcols) && gcols >= 1 && gcols <= GRID_COLUMNS_MAX) s.style.grid_columns = gcols;
  // `asl` is the name it had when it shipped as "axis sliders"; old links keep it.
  if (params.get('pz') === '1' || params.get('asl') === '1') s.style.precision_zoom = true;
  const ll = params.get('ll');
  if (ll === 'end' || ll === 'center' || ll === 'now') s.style.live_layout = ll;
  if (params.get('lnl') === '0') s.style.live_now_label = false;
  const lm = params.get('lm');
  if (lm === 'smooth' || lm === 'steps' || lm === 'off') s.style.live_motion = lm;

  const colors = params.get('colors');
  if (colors) {
    const map: Record<string, string> = {};
    for (const pair of colors.split(',')) {
      const idx = pair.lastIndexOf(':');
      if (idx < 0) continue;
      const col = pair.slice(0, idx);
      const hex = pair.slice(idx + 1);
      if (/^[0-9a-fA-F]{6}$/.test(hex)) map[col] = '#' + hex.toLowerCase();
    }
    s.custom_colors = map;
  }

  const fStr = params.get('f');
  if (fStr) s.fits = parseFitsParam(fStr);

  const parseBound = (key: string): number | null => {
    const v = params.get(key);
    if (v === null) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  s.axis_bounds = {
    y_min: parseBound('ymin'),
    y_max: parseBound('ymax'),
    yr_min: parseBound('yrmin'),
    yr_max: parseBound('yrmax'),
    x_min: parseBound('xmino'),
    x_max: parseBound('xmaxo'),
  };
  s.axis_titles = { y: parseAxisTitle(params.get('yt')), yr: parseAxisTitle(params.get('yrt')) };

  return s;
}

/**
 * Parse the `d` derived-signal param. Three wire formats, all supported:
 *   expr: name|EXPR|<expression>
 *   new:  name|op|aMode|a|bMode|b  (6 segments)
 *   old:  name|op|a|bMode|b        (5 segments, implicit aMode='c')
 */
export function parseDerivedParam(str: string): DerivedSignal[] {
  const out: DerivedSignal[] = [];
  for (const entry of str.split(',,')) {
    const parts = entry.split('|');
    // Expression mode - the rest (rejoined) is the free-form expression.
    if (parts.length >= 3 && parts[1] === 'EXPR' && parts[0]) {
      out.push({
        name: parts[0],
        op: '*',
        operand_a: null,
        operand_a_const: null,
        operand_b_col: null,
        operand_b_const: null,
        expr: parts.slice(2).join('|'),
      });
      continue;
    }
    if (parts.length < 5) continue;
    let name: string, op: string, aMode: string, a: string, bMode: string, b: string;
    if (parts.length >= 6) {
      [name, op, aMode, a, bMode, b] = parts as [string, string, string, string, string, string];
    } else {
      [name, op, a, bMode, b] = parts as [string, string, string, string, string];
      aMode = 'c';
    }
    if (!name || !op) continue;
    const d: DerivedSignal = {
      name,
      op: op as DerivedSignal['op'],
      operand_a: null,
      operand_a_const: null,
      operand_b_col: null,
      operand_b_const: null,
    };
    if (aMode === 'c') d.operand_a = a || '';
    else d.operand_a_const = Number(a);
    if (bMode === 'c') d.operand_b_col = b || '';
    else d.operand_b_const = Number(b);
    out.push(d);
  }
  return out;
}

/**
 * Parse the `ol` outlier param (`method|action|col1,col2|k=…;ns=…;pl=…;ph=…;w=…`)
 * into a full `OutlierState`, merging defaults for anything absent/invalid.
 */
export function parseOutliersParam(str: string): OutlierState {
  const out: OutlierState = { ...DEFAULT_OUTLIERS };
  const [method, action, cols, paramStr] = str.split('|');
  const methods: OutlierMethod[] = ['iqr', 'zscore', 'percentile', 'mad', 'hampel'];
  if (!method || !methods.includes(method as OutlierMethod)) return out;
  out.method = method as OutlierMethod;
  out.action = action === 'null' ? 'null' : 'drop';
  out.columns = (cols ?? '').split(',').filter(Boolean);
  if (paramStr) {
    for (const pv of paramStr.split(';')) {
      const eq = pv.indexOf('=');
      if (eq < 0) continue;
      const key = pv.slice(0, eq);
      const v = Number(pv.slice(eq + 1));
      if (!Number.isFinite(v)) continue;
      if (key === 'k') out.k = v;
      else if (key === 'ns') out.nSigma = v;
      else if (key === 'pl') out.pLow = v;
      else if (key === 'ph') out.pHigh = v;
      else if (key === 'w') out.window = v;
    }
  }
  return out;
}

/** Parse the `f` fits param (`series|kind|p=v;…|hex`). */
export function parseFitsParam(str: string): Fit[] {
  const out: Fit[] = [];
  for (const entry of str.split(',,')) {
    const parts = entry.split('|');
    if (parts.length < 2) continue;
    const [series, kind, paramStr, colorHex] = parts;
    if (!series || !kind) continue;
    const p: Record<string, number> = {};
    if (paramStr) {
      for (const pv of paramStr.split(';')) {
        const eq = pv.indexOf('=');
        if (eq < 0) continue;
        const k = pv.slice(0, eq);
        const v = Number(pv.slice(eq + 1));
        if (Number.isFinite(v)) p[k] = v;
      }
    }
    const fit: Fit = { series, kind, params: p };
    if (colorHex && /^[0-9a-fA-F]{6}$/.test(colorHex)) fit.color = '#' + colorHex.toLowerCase();
    out.push(fit);
  }
  return out;
}

/**
 * Parse the `filters` param (`col|op|val` joined by `;;`) into filter rows. Like
 * the blob, the value is everything-up-to-the-second-pipe (`split('|')[2]`), and
 * an unknown operator falls back to `==`.
 */
export function parseFiltersParam(str: string): FilterRow[] {
  const out: FilterRow[] = [];
  let i = 0;
  for (const cond of str.split(';;')) {
    const parts = cond.split('|');
    const col = parts[0];
    if (!col) continue;
    const rawOp = parts[1] ?? '==';
    const rhs = parts[2] ?? '';
    if (rawOp === '~') {
      const [lo, hi] = rhs.split('~');
      const num = (v: string | undefined): number | null => {
        if (v == null || v === '') return null;
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
      };
      out.push({ id: `url${i++}`, column: col, op: '>=', value: '', slicer: { min: num(lo), max: num(hi) } });
      continue;
    }
    const op = FILTER_OPS.includes(rawOp as FilterOp) ? (rawOp as FilterOp) : '==';
    if (rhs.startsWith('@')) {
      out.push({ id: `url${i++}`, column: col, op, value: '', value_column: rhs.slice(1) });
    } else {
      out.push({ id: `url${i++}`, column: col, op, value: rhs });
    }
  }
  return out;
}

/* ── Cross-ship column pruning ───────────────────────────── */

/** Column names valid on the current ship - raw columns plus derived names. */
function availableColumnNames(): Set<string> {
  const set = new Set(allColumns.value.map((c) => c.name));
  for (const d of derivedSignals.value) set.add(d.name);
  return set;
}

/**
 * Drop any picked plot columns the loaded ship doesn't have. Matters when a
 * config-only saved view is applied to a different ship than it was built on
 * (mirrors the blob's onColumnsLoadedForPlot prune), and when the data source
 * changes under the picks. With no ship loaded nothing is available, so every
 * pick goes.
 */
export function prunePlotColumns(): void {
  const avail = availableColumnNames();
  const s = plotState.value;
  const defaultTimeCol = 'timestampNDC';
  const yl = s.y_left.filter((c) => avail.has(c));
  const yr = s.y_right.filter((c) => avail.has(c));
  const hc = s.hist_columns.filter((c) => avail.has(c));
  const ccol = s.corr_columns.filter((c) => avail.has(c));
  const dials = s.gauge_dials.map((d) =>
    d.mode === 'signal' && d.column && !avail.has(d.column) ? { ...d, column: '' } : d,
  );
  const sknodes = s.sankey_nodes.map((n) =>
    n.kind === 'signal' && n.column && !avail.has(n.column) ? { ...n, column: '' } : n,
  );
  const pcol = s.pie_columns.filter((c) => avail.has(c));
  const pbc = s.pie_band_column && !avail.has(s.pie_band_column) ? '' : s.pie_band_column;
  const ncol = s.number_column && !avail.has(s.number_column) ? '' : s.number_column;
  const bcols = s.box_columns.filter((c) => avail.has(c));
  const bcol = s.box_column && !avail.has(s.box_column) ? '' : s.box_column;
  const bgrp = s.box_group_column && !avail.has(s.box_group_column) ? '' : s.box_group_column;
  const x = avail.has(s.x_column) ? s.x_column : defaultTimeCol;
  const cb = s.color_by && !avail.has(s.color_by) ? '' : s.color_by;
  const lat = s.lat_column && !avail.has(s.lat_column) ? '' : s.lat_column;
  const lon = s.lon_column && !avail.has(s.lon_column) ? '' : s.lon_column;
  const hy = s.y_column && !avail.has(s.y_column) ? '' : s.y_column;
  const hz = s.z_column && !avail.has(s.z_column) ? '' : s.z_column;
  const table = pruneTable(s, avail);
  if (
    table ||
    yl.length !== s.y_left.length ||
    yr.length !== s.y_right.length ||
    hc.length !== s.hist_columns.length ||
    ccol.length !== s.corr_columns.length ||
    dials.some((d, i) => d !== s.gauge_dials[i]) ||
    sknodes.some((n, i) => n !== s.sankey_nodes[i]) ||
    pcol.length !== s.pie_columns.length ||
    pbc !== s.pie_band_column ||
    bcols.length !== s.box_columns.length ||
    bcol !== s.box_column ||
    bgrp !== s.box_group_column ||
    ncol !== s.number_column ||
    x !== s.x_column ||
    cb !== s.color_by ||
    lat !== s.lat_column ||
    lon !== s.lon_column ||
    hy !== s.y_column ||
    hz !== s.z_column
  ) {
    plotState.set({
      ...s,
      y_left: yl,
      y_right: yr,
      hist_columns: hc,
      corr_columns: ccol,
      gauge_dials: dials,
      sankey_nodes: sknodes,
      pie_columns: pcol,
      pie_band_column: pbc,
      box_columns: bcols,
      box_column: bcol,
      box_group_column: bgrp,
      number_column: ncol,
      x_column: x,
      color_by: cb,
      lat_column: lat,
      lon_column: lon,
      y_column: hy,
      z_column: hz,
      ...table,
    });
  }
}

/* ── restoreFromUrl: query string → atoms ────────────────── */

/**
 * Restore the view from a query string (or `window.location.search` when none is
 * passed). Returns immediately if there are no params. Writes atoms in the same
 * careful order as the blob - plot state seeded first, `sel`/filters after the
 * ship's columns load, derived signals before filters - with `syncToUrl()`
 * suspended throughout so the URL is rewritten exactly once at the end.
 */
export async function restoreFromUrl(
  queryStr?: string,
  opts: { visibleUrl?: string } = {},
): Promise<void> {
  const source =
    queryStr !== undefined ? String(queryStr).replace(/^\?/, '') : window.location.search;
  const params = new URLSearchParams(source);
  // Set before the empty-params early return: a plain load with no query has
  // nothing to restore, but syncing must still be unblocked afterwards.
  urlRestored = true;
  if ([...params.keys()].length === 0) {
    syncToUrl(opts.visibleUrl);
    return;
  }

  suspendDepth++;
  try {
    // Every restore is authoritative. A regular link/view clears both an active
    // comparison and its suspended session draft before applying its own state.
    clearPeriodComparison();
    // Plot state (everything except dates/filters/derived, which have their own atoms).
    plotState.set(parsePlotState(params, plotState.value));

    // Derived signals - before filters so they're recognized as filter columns.
    const dStr = params.get('d');
    if (dStr) derivedSignals.set(parseDerivedParam(dStr));

    // Outlier filter (off by default - only present when the view had one active).
    const olStr = params.get('ol');
    if (olStr) outliers.set(parseOutliersParam(olStr));

    // Section - before the tab, since Dashboards hides the tab chrome entirely.
    const sectionParam = params.get('section');
    if (
      sectionParam === 'home' ||
      sectionParam === 'dashboards' ||
      sectionParam === 'explore' ||
      sectionParam === 'catalog' ||
      (sectionParam === 'assistant' && isPageVisible('assistant'))
    ) {
      activeSection.set(sectionParam);
      document.body.dataset.section = sectionParam;
    } else if (sectionRoute(sectionParam)) {
      // A section a feature registered (see `registerSectionRoute`). The
      // shell's visibility check decides whether this person may stay on it.
      const registered = sectionRoute(sectionParam) as SectionRoute;
      activeSection.set(registered.section);
      document.body.dataset.section = registered.section;
    }

    const atab = params.get('atab');
    if (atab === 'history' || atab === 'chat') {
      assistantTab.set(atab);
      document.body.dataset.assistantTab = atab;
    }

    // Data source - restore before the tab/ship path so the per-source gates
    // (and the `eos-only` CSS, keyed off the body attribute) are correct.
    // Any well-formed id: an unknown or unavailable one says so in the Data
    // source step and when its ships fail to load, and EOS is one click away.
    const ds = params.get('ds');
    if (ds && /^[a-z][a-z0-9_-]{0,39}$/.test(ds)) {
      activeDataSource.set(ds);
      document.body.dataset.datasource = ds;
    }

    // Active tab - set the atom (drives the tab bar) and the body attribute that
    // the per-tab visibility CSS keys off (app-root sets this imperatively too).
    // Every tab restore is gated on the page being visible: a flag can switch
    // one off for this account, and a link written before that - or by somebody
    // it is still on for - must not park them on a tab with no button.
    // `app-root.enforceVisibility` is the backstop for the other order, when
    // the flags land after this ran.
    const tabParam = params.get('tab');
    if (tabParam === 'export' && isPageVisible('export')) {
      activeTab.set('export');
      document.body.dataset.activeTab = 'export';
    } else if (tabParam === 'plot' && isPageVisible('plot')) {
      // Plot is implicit in normal shared URLs because it is the default tab,
      // but callers such as assistant proposals set it explicitly to navigate
      // back from another tab when their configuration is applied.
      activeTab.set('plot');
      document.body.dataset.activeTab = 'plot';
    } else if (['modelviewer', 'spmodel'].includes(tabParam ?? '') && isPageVisible('modelviewer')) {
      // `spmodel` is the legacy deep link for the now-nested Service Power view.
      activeTab.set('modelviewer');
      document.body.dataset.activeTab = 'modelviewer';
      const mt = params.get('mt');
      if (mt === 'sp' || mt === 'sfoc') {
        activeModelTab.set(mt);
        document.body.dataset.modelTab = mt;
      }
    } else if (tabParam === 'assistant' && isPageVisible('assistant')) {
      // The assistant left the tab bar and became a section of its own, so an
      // older `?tab=assistant` link lands there instead of on a tab that no
      // longer exists. Still gated: its flag, or the permission that stands in
      // for it, or a stray link would expose it to everyone else.
      activeSection.set('assistant');
      document.body.dataset.section = 'assistant';
    } else if (!tabParam && params.has('pc')) {
      // Plot is normally implicit. Saved-view loading temporarily parks on
      // Export before restore, so an implicit comparison must make that default
      // explicit again before compatibility validation below.
      activeTab.set('plot');
      document.body.dataset.activeTab = 'plot';
    }

    // Model Viewer selection - its own ships and window, shared by every model,
    // restored independently of the Export/Plot ship+columns path; then SFOC's
    // curve keys. Older links spoke for themselves: an SFOC link's `sfd` curve
    // date becomes the 30 days ending on it and its curve keys name its ships,
    // and a Service Power link's `ship` / `start` / `end` land on Explore's
    // atoms below, which is what the Model Viewer starts from anyway
    // (`seedFromExplore`).
    const mvp = params.get('mvp');
    const mvst = params.get('mvst');
    const mven = params.get('mven');
    const sfd = params.get('sfd');
    const mvs = (params.get('mvs') ?? '').split(',').filter(Boolean);
    const sfs = (params.get('sfs') ?? '').split(',').filter(Boolean);
    const named = mvp ? presetWindow(mvp) : null;
    let modelWindow: { start: string; end: string; preset: DatePreset } | null = null;
    if (mvp && named) modelWindow = { ...named, preset: mvp as DatePreset };
    else if (mvst && mven) {
      modelWindow = { start: toDatetimeLocal(mvst), end: toDatetimeLocal(mven), preset: 'custom' };
    } else if (sfd && /^\d{4}-\d{2}-\d{2}$/.test(sfd)) {
      const dayAfter = new Date(`${sfd}T00:00:00Z`);
      dayAfter.setUTCDate(dayAfter.getUTCDate() + 1);
      modelWindow = { start: offsetDays(dayAfter, -30), end: offsetDays(dayAfter, 0), preset: 'custom' };
    }
    if (modelWindow) {
      modelStart.set(modelWindow.start);
      modelEnd.set(modelWindow.end);
      modelPreset.set(modelWindow.preset);
    }
    const shipsFromKeys = sfs.map((key) => key.split('‖')[0] ?? '').filter(Boolean);
    const pickedModelShips = [...new Set([...mvs, ...shipsFromKeys])];
    if (pickedModelShips.length) modelShips.set(pickedModelShips);
    if (sfs.length) sfocSelected.set(new Set(sfs));
    const sfm = params.get('sfm');
    if (sfm === 'off' || sfm === 'scatter' || sfm === 'heatmap' || sfm === 'load') sfocMeasured.set(sfm);
    if (params.has('sfl')) sfocLayout.set(params.get('sfl') === 'grid' ? 'grid' : 'overlay');

    // Ship - the list is already loaded (bootstrap awaits loadShips first), so
    // just set the selection and pull its columns. loadColumns clears selectedCols.
    const ship = params.get('ship');
    if (ship) {
      selectedShip.set(ship);
      await loadColumns(ship);
      // Prune plot picks to the ship's schema (+ derived names) so a config-only
      // saved view applied to a different ship doesn't carry phantom columns.
      prunePlotColumns();
    }

    // Comparison ships. Restored after the anchor so `plottedShipIds()` is
    // anchor-first, which is what keeps each ship's colour stable.
    const extras = (params.get('ships') ?? '')
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s && s !== ship);
    comparisonShips.set(extras.slice(0, MAX_PLOT_SHIPS - 1));
    if (extras.length) await ensureShipColumns(plottedShipIds());

    // Export-side selected columns - AFTER loadColumns (which clears them). Drop
    // names not in this ship's schema so a cross-ship preset can't carry phantoms.
    const selStr = params.get('sel');
    if (selStr && allColumns.value.length > 0) {
      const available = new Set(allColumns.value.map((c) => c.name));
      selectedCols.set(new Set(selStr.split(',').filter(Boolean).filter((n) => available.has(n))));
    }

    // Dates - a named preset recomputes its bounds; otherwise custom start/end.
    const preset = params.get('preset');
    if (preset) {
      applyDatePreset(preset as DatePreset);
    } else {
      const start = params.get('start');
      const end = params.get('end');
      if (start) startDate.set(toDatetimeLocal(start));
      if (end) endDate.set(toDatetimeLocal(end));
      setPresetActive('custom');
    }

    const pc = params.get('pc');
    if (pc) {
      const parsed = parsePeriodComparison(pc);
      const compatible =
        activeTab.value === 'plot' &&
        activeDataSource.value === DEFAULT_SOURCE &&
        (plotState.value.plot_type === 'line' || plotState.value.plot_type === 'scatter') &&
        !!selectedShip.value &&
        comparisonShips.value.length === 0;
      const issues = parsed.value ? comparisonIssues(parsed.value) : [];
      if (parsed.value && compatible && !issues.length) {
        periodComparison.set(parsed.value);
        setPresetActive('custom');
      } else {
        periodComparisonNotice.set(
          parsed.error ?? issues[0]?.message ?? 'This saved comparison requires one ship and a historical Line or Scatter plot.',
        );
      }
    }

    // Export options.
    const fmt = params.get('format');
    if (fmt === 'csv' || fmt === 'parquet' || fmt === 'xlsx') exportFormat.set(fmt);
    const freq = params.get('freq');
    if (freq) {
      const m = freq.match(/^(\d+)(min|h|d)$/i);
      if (m) {
        frequencyValue.set(m[1] ?? '');
        frequencyUnit.set((m[2] ?? 'min').toLowerCase() as 'min' | 'h' | 'd');
      }
    }
    const fill = params.get('fill');
    if (fill === 'none' || fill === 'forward' || fill === 'backward') fillMethod.set(fill);
    const logic = params.get('logic');
    if (logic === 'AND' || logic === 'OR') filterLogic.set(logic);

    // Filters - after columns are loaded. When we have a schema, drop conditions
    // whose column isn't on this ship (config-only cross-ship views). When no
    // columns are loaded yet (e.g. a config-only view loaded before any ship is
    // selected), restore them UNPRUNED rather than dropping them silently - they
    // get re-validated on the next estimate once a ship's columns arrive. Losing
    // filters here was why imported views didn't fully persist their filters.
    const filtersStr = params.get('filters');
    if (filtersStr) {
      const parsed = parseFiltersParam(filtersStr);
      if (allColumns.value.length > 0) {
        const available = availableColumnNames();
        filters.set(
          parsed.filter(
            (f) => available.has(f.column) && (!f.value_column || available.has(f.value_column)),
          ),
        );
      } else {
        filters.set(parsed);
      }
    }

    // Experimental filter expression - restore the text and recompute its
    // validity so the status line is correct without waiting for an edit.
    const fexpr = params.get('fexpr');
    if (fexpr) {
      filterExpr.set(fexpr);
      filterExprError.set(validateExpr(fexpr, exprKindOf));
    }
    // Filters are one list now - rows and an expression, ANDed. Older links came
    // from the Builder/Expr switch, where only one half was live: restore just
    // that half, so the link still shows what it showed when it was shared.
    const fmode = params.get('fmode');
    if (fmode !== 'and' && fexpr && filtersStr) {
      if (fmode === 'expr') filters.set([]);
      else {
        filterExpr.set('');
        filterExprError.set(null);
      }
    }
    filterMode.set('and');

    scheduleEstimate();

  } finally {
    suspendDepth--;
  }
  // One write reflecting the fully-restored state (and normalizes legacy params).
  syncToUrl(opts.visibleUrl);
}
